<script>
import { mapGetters } from 'vuex';
import { useTrack } from 'dashboard/composables';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';

import NotificationTable from './NotificationTable.vue';
import AttendanceReportNotifications from 'dashboard/components-next/Attendances/ReportNotifications.vue';
import aceleresIntegrations from 'dashboard/api/aceleres/integrations';
import {
  SIDEBAR_PAGE_IDS,
  buildNotificationsDefaultSidebarPageKey,
  isSidebarPageReady,
  markSidebarPageReady,
} from 'dashboard/helper/sidebarPagePreload';

import { ACCOUNT_EVENTS } from '../../../../helper/AnalyticsHelper/events';
export default {
  data() {
    return {
      whatsappKeyNotifications: [],
      whatsappKeyNotificationsError: false,
      whatsappKeyTimer: null,
    };
  },
  components: {
    NotificationTable,
    AttendanceReportNotifications,
    TableFooter,
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      meta: 'notifications/getMeta',
      records: 'notifications/getNotifications',
      uiFlags: 'notifications/getUIFlags',
    }),
  },
  mounted() {
    this.loadWhatsappKeyNotifications();
    this.whatsappKeyTimer = window.setInterval(
      this.loadWhatsappKeyNotifications,
      20000
    );
    const cacheKey = buildNotificationsDefaultSidebarPageKey();
    if (
      Number(this.$route.query?.page || 1) === 1 &&
      isSidebarPageReady(SIDEBAR_PAGE_IDS.NOTIFICATIONS_DEFAULT, cacheKey)
    ) {
      return;
    }

    this.$store.dispatch('notifications/get', { page: 1 }).then(() => {
      markSidebarPageReady(SIDEBAR_PAGE_IDS.NOTIFICATIONS_DEFAULT, cacheKey);
    });
  },
  beforeUnmount() {
    if (this.whatsappKeyTimer) window.clearInterval(this.whatsappKeyTimer);
  },
  methods: {
    async loadWhatsappKeyNotifications() {
      try {
        const state = await aceleresIntegrations.getKeyEntitlements();
        this.whatsappKeyNotifications = state.notifications || [];
        this.whatsappKeyNotificationsError = false;
      } catch {
        this.whatsappKeyNotificationsError = true;
      }
    },
    async openWhatsappKeyNotification(notification) {
      try {
        const notificationUrl = new URL(
          notification.url || '',
          window.location.origin
        );
        const isPendingManagerRequest =
          notificationUrl.searchParams.get('aba') === 'chaves' &&
          Number(notificationUrl.searchParams.get('pedido')) > 0;
        if (!isPendingManagerRequest) {
          await aceleresIntegrations.readKeyNotification(notification.id);
        }
      } finally {
        window.location.href = notification.url;
      }
    },
    formatWhatsappKeyTitle(title) {
      return String(title || '').replace(/\+(\d+) chave\(s\)/g, (_, value) => {
        const quantity = Number(value);
        return `+${quantity} ${quantity === 1 ? 'chave' : 'chaves'}`;
      });
    },
    formatWhatsappKeyBody(body) {
      const text = String(body || '');
      const approval = text.match(
        /^Agora você tem (\d+) conexões liberadas\.$/
      );
      if (approval) {
        const quantity = Number(approval[1]);
        return `Agora você tem ${quantity} ${quantity === 1 ? 'conexão liberada' : 'conexões liberadas'}.`;
      }

      const limit = text.match(/^(\d+) de (\d+) conexões liberadas em uso\.$/);
      if (limit) {
        const used = Number(limit[1]);
        const allowed = Number(limit[2]);
        return `${used} de ${allowed} ${allowed === 1 ? 'conexão liberada' : 'conexões liberadas'} em uso.`;
      }

      return text;
    },
    formatWhatsappKeyDateTime(value) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      return new Intl.DateTimeFormat('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(date);
    },
    whatsappKeyUnreadLabel(quantity) {
      return `${quantity} ${quantity === 1 ? 'nova' : 'novas'}`;
    },
    onPageChange(page) {
      window.history.pushState({}, null, `${this.$route.path}?page=${page}`);
      this.$store.dispatch('notifications/get', { page }).then(() => {
        if (page === 1) {
          markSidebarPageReady(
            SIDEBAR_PAGE_IDS.NOTIFICATIONS_DEFAULT,
            buildNotificationsDefaultSidebarPageKey()
          );
        }
      });
    },
    openConversation(notification) {
      const {
        primary_actor_id: primaryActorId,
        primary_actor_type: primaryActorType,
        primary_actor: { id: conversationId },
        notification_type: notificationType,
      } = notification;

      useTrack(ACCOUNT_EVENTS.OPEN_CONVERSATION_VIA_NOTIFICATION, {
        notificationType,
      });
      this.$store.dispatch('notifications/read', {
        id: notification.id,
        primaryActorId,
        primaryActorType,
        unreadCount: this.meta.unreadCount,
      });

      this.$router.push(
        `/app/accounts/${this.accountId}/conversations/${conversationId}`
      );
    },
    onMarkAllDoneClick() {
      useTrack(ACCOUNT_EVENTS.MARK_AS_READ_NOTIFICATIONS);
      this.$store.dispatch('notifications/readAll');
    },
  },
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div class="h-full overflow-y-auto">
    <div class="flex flex-col h-full">
      <AttendanceReportNotifications />
      <section
        v-if="whatsappKeyNotifications.length || whatsappKeyNotificationsError"
        class="border-b border-n-weak bg-n-solid-1 p-4"
      >
        <h2 class="mb-3 text-lg font-semibold text-n-slate-12">
          Pedidos de chaves de WhatsApp
          <span
            v-if="whatsappKeyNotifications.filter(item => !item.read_at).length"
            class="ml-2 rounded-full bg-n-brand/10 px-2 py-0.5 text-xs text-n-blue-11"
          >
            {{
              whatsappKeyUnreadLabel(
                whatsappKeyNotifications.filter(item => !item.read_at).length
              )
            }}
          </span>
        </h2>
        <div
          v-if="whatsappKeyNotificationsError"
          class="mb-3 rounded-xl bg-n-amber-2 p-3 text-sm text-n-amber-11"
          role="alert"
        >
          <span
            >Não foi possível carregar as notificações de chaves de
            WhatsApp.</span
          >
          <button
            type="button"
            class="ml-2 font-medium underline"
            @click="loadWhatsappKeyNotifications"
          >
            Tentar novamente
          </button>
        </div>
        <button
          v-for="item in whatsappKeyNotifications"
          :key="item.id"
          type="button"
          class="mb-2 block w-full rounded-xl border border-n-weak p-3 text-left transition-colors hover:bg-n-alpha-1"
          :class="item.read_at ? '' : 'bg-n-brand/5'"
          @click="openWhatsappKeyNotification(item)"
        >
          <span class="flex items-start justify-between gap-3">
            <strong
              class="min-w-0 text-sm text-n-slate-12 [overflow-wrap:anywhere]"
              >{{ formatWhatsappKeyTitle(item.title) }}</strong
            >
            <span
              v-if="!item.read_at"
              class="inline-flex shrink-0 items-center gap-1 rounded-full bg-n-brand/10 px-2 py-0.5 text-xs font-medium text-n-blue-11"
            >
              <span
                aria-hidden="true"
                class="size-1.5 rounded-full bg-n-brand"
              />Não lida
            </span>
          </span>
          <span
            v-if="formatWhatsappKeyBody(item.body)"
            class="mt-1 block text-sm text-n-slate-11 [overflow-wrap:anywhere]"
            >{{ formatWhatsappKeyBody(item.body) }}</span
          >
          <span
            class="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1"
          >
            <time class="text-xs text-n-slate-10" :datetime="item.created_at">{{
              formatWhatsappKeyDateTime(item.created_at)
            }}</time>
            <span class="text-sm font-medium text-n-blue-11">{{
              item.url.includes('aba=chaves')
                ? 'Ver em Chaves de instância →'
                : item.title.startsWith('Pedido recusado')
                  ? 'Fazer novo pedido →'
                  : 'Conectar WhatsApp →'
            }}</span>
          </span>
        </button>
      </section>
      <NotificationTable
        :notifications="records"
        :is-loading="uiFlags.isFetching"
        :is-updating="uiFlags.isUpdating"
        :on-click-notification="openConversation"
        :on-mark-all-done-click="onMarkAllDoneClick"
      />
      <TableFooter
        class="border-t border-n-weak"
        :current-page="Number(meta.currentPage)"
        :total-count="meta.count"
        :page-size="15"
        @page-change="onPageChange"
      />
    </div>
  </div>
</template>
